/**
 * @module Service
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { AppContext } from '@app/models/app-context.model';
import { AuthService } from '@app/services/auth.service';
import { CompanyService } from '@feature/companies/shared/company.service';
import { User, UserService } from '@feature/users/shared';
import { CompanyMemberRef, CompanyMemberRequestStatus } from '@feature/companies/shared';
import { SnackBarService } from '@app/services/snackbar.service';
import { UtilsService } from '@app/services/utils.service';

/**
 * Service that manages the user context within the application.
 *
 * This service provides access to the current active context, allowing components
 * to reactively respond to changes in user information and application state. It also
 * facilitates navigation and messaging between components.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class AppContextService {

	/**
	 * A BehaviorSubject that holds the current active user context.
	 *
	 * This subject allows subscribers to receive updates whenever the context changes.
	 *
	 * @type {BehaviorSubject<AppContext>}
	 */
	public currentContext: BehaviorSubject<AppContext> = new BehaviorSubject<AppContext>(null);

	/**
	 * Indicates whether the user is being changed locally without needing to subscribe to changes.
	 *
	 * @type {boolean}
	 */
	private localUserChange: boolean;

	/**
	 * Creates an instance of the AppContextService.
	 *
	 * @constructor
	 * @param {Router} _router - The Angular Router used for navigation.
	 * @param {ActivatedRoute} _route - The activated route for accessing route parameters and data.
	 * @param {UtilsService} _utilsService - Utility service for common operations.
	 * @param {SnackBarService} _snackbarService - Service for displaying snack bar messages.
	 * @param {AuthService} _authService - Service for managing authentication state.
	 * @param {UserService} _userService - Service for managing user-related operations.
	 * @param {CompanyService} _companyService - Service for managing company-related operations.
	 */
	constructor(
        private _router: Router,
		private _route: ActivatedRoute,
		private _utilsService: UtilsService,
		private _snackbarService: SnackBarService,
        private _authService: AuthService,
        private _userService: UserService,
        private _companyService: CompanyService
	) {
		this.localUserChange = false;
		this._authService.apiUser$
			.pipe(filter(user => user !== null && ! this.localUserChange))
			.subscribe((user: User) => {
				const ctxStr = localStorage.getItem('currentContext');
				if (ctxStr !== null) { // If context found, use it.
					const ctx: AppContext = Object.assign(new AppContext(), JSON.parse(ctxStr));
					ctx.user = user;
					if (ctx.companyMembership) {
						// ETT-230 Fetch the up-to-date ref.
						this._companyService.getRefByCompanyIdAndMemberId(ctx.companyMembership.companyId, ctx.companyMembership.memberId).subscribe({
							next: (ref: CompanyMemberRef) => {
								ctx.companyMembership = Object.assign(new CompanyMemberRef(), ref);
								this.currentContext.next(ctx);
							},
							error: (error: any) => {
								if (error.status === 403) {
									this._setInitialContext(user, ctx.companyMembership);
								} else {
									this._setInitialContext(user);
								}
							}
						});
					} else {
						this.currentContext.next(ctx);
					}
				} else { // If no context set, yet, search for it.
					this._setInitialContext(user);
				}
			});
	}

	/**
	 * Sets the initial context for the provided company member.
	 *
	 * This method retrieves the companies associated with the user and sets the default company context
	 * if available. If the user is not allowed to use a specific context, a warning message is displayed.
	 *
	 * @param {User} user - The user for whom the context is being set.
	 * @param {CompanyMemberRef} [notAllowed] - Optional reference to a company member that
	 *                                          the user is not allowed to use due to the company permissions.
	 * @returns {void}
	 *
	 * @private
	 * @hidden
	 */
	private _setInitialContext(user: User, notAllowed?: CompanyMemberRef): void {
		if (user?.id == null) return;
		this._companyService.getCompaniesByMemberId(user.id)
			.pipe(filter((cmr: Array<CompanyMemberRef>) => cmr != null))
			.subscribe((companiesMembership: Array<CompanyMemberRef>) => {
				const accepted: Array<CompanyMemberRef> = companiesMembership.filter((ref: CompanyMemberRef) => ref.status === CompanyMemberRequestStatus.accepted);
				let newCtxName: string = '';
				if (accepted?.length > 0) { // Use first company as default if not already set.
					const memberRef: CompanyMemberRef = Object.assign(new CompanyMemberRef(), accepted[0]);
					this.setDefaultCompanyContext(memberRef);
					newCtxName = memberRef.companyName;
				} else {
					this.setPersonalContext(true);
					newCtxName = 'Personal';
				}
				if (notAllowed) {
					this._snackbarService.warn(`You are not allowed to use the "${notAllowed.companyName}" context due to permissions. Switching to "${newCtxName}" instead.`);
				}
			});
	}

	/**
	 * Sets the company context for the current user.
	 *
	 * This method updates the application context to reflect the specified company member reference.
	 * It can optionally skip reloading the current route.
	 *
	 * @param {CompanyMemberRef} company - The company member reference to set as the current context.
	 * @param {boolean} [skipReload=false] - Optional flag to skip reloading the current route.
	 * @returns {void}
	 */
	public setCompanyContext(company: CompanyMemberRef, skipReload: boolean = false): void {
		this._setContext(company, skipReload);
	}

	/**
	 * Sets the default company context based on the provided company member relationship.
	 *
	 * This method checks if the current context is not already set and updates it accordingly.
	 *
	 * @param {CompanyMemberRef} company - The company member reference to set as the default context.
	 * @returns {void}
	 */
	public setDefaultCompanyContext(company: CompanyMemberRef): void {
		if (! this.currentContext.value) {
			this.setCompanyContext(company, true);
		}
	}

	/**
	 * Sets the context as for the individual for the current user.
	 *
	 * This method updates the application context to indicate that the user is in a personal context,
	 * optionally skipping the reload of the current route.
	 *
	 * @param {boolean} [skipReload=false] - Optional flag to skip reloading the current route.
	 * @returns {void}
	 */
	public setPersonalContext(skipReload: boolean = false): void {
		this._setContext(undefined, skipReload);
	}

	/**
	 * Refreshes the current context by updating the user information and potentially reloading the route.
	 *
	 * This method retrieves the current user context and updates the user details. It handles both
	 * individual and company contexts, ensuring that the user state is accurately reflected in the application.
	 *
	 * @returns {Promise<boolean>} A promise that resolves to true if the context was refreshed successfully, otherwise false.
	 */
	public refreshContext(): Promise<boolean> {
		const ctx = this.currentContext.value;
		return new Promise<boolean>((resolve, reject) => {
			if (ctx) {
				// Update User either way - this is to get User.isCompany correct.
				const sub: Subscription = this._userService.getById(ctx.user.id.toString())
					.subscribe({
						next: (updated: User) => {
							// @todo Test how the localUserChange works.
							this._authService.apiUser = Object.assign(new User(), updated);
							this.localUserChange = false;
							if (ctx.isPersonalContext) {
								this.setPersonalContext(); // E.g. after the profile edits, personal context has to be refreshed.
								resolve(true);
							}
							sub.unsubscribe();
						},
						error: (error: any) => reject(error.message)
					});

				if (ctx.isCompanyContext) {
					// @note Capture that user belongs to a company, or checks within platform won't work.
					this._authService.apiUser.isCompany = true;
					const companySub: Subscription = this._companyService.getRefByCompanyIdAndMemberId(ctx.companyMembership.companyId, ctx.user.id)
						.subscribe({
							next: (updated: CompanyMemberRef) => {
								updated = Object.assign(new CompanyMemberRef(), updated);
								this.setCompanyContext(updated);
								resolve(true);
								companySub.unsubscribe();
							},
							error: (error: any) => reject(error.message)
						});
				}
			}
		});
	}

	/**
	 * Reloads the current route, potentially updating guards and parameters.
	 *
	 * This method navigates to the current URL without query parameters, allowing for re-evaluation
	 * of route guards and other conditions based on the updated context.
	 *
	 * @returns {void}
	 */
	public reload(): void {
		const newUrl = this._utilsService.getCurrentUrlWithoutQueryParamsVanillaJS(this._router.url);
		const newQueryParams: Params = {};
		this._route.queryParamMap.forEach((params: ParamMap) => {
			params.keys.forEach((paramKey: string) => {
				newQueryParams[paramKey] = null;
			});
		});

		// When the context changes, we might need to re-evaluate the Guards like SignupGuard.
		// @see [Reloading current route](https://medium.com/engineering-on-the-incline/reloading-current-route-on-click-angular-5-1a1bfc740ab2)
		this._router.navigate([newUrl], {
			queryParams: newQueryParams,
			queryParamsHandling: 'merge'
		});
	}

	/**
	 * Sets the context for the current user based on the individual or company information.
	 *
	 * This method updates the application context and optionally reloads the current route.
	 *
	 * @param {CompanyMemberRef} [company] - The company member reference to set as the current context.
	 * @param {boolean} [skipReload=false] - Optional flag to skip reloading the current route.
	 * @returns {void}
	 */
	private _setContext(company?: CompanyMemberRef, skipReload: boolean = false): void {
		this._authService.apiUser$
			.pipe(first((user: User) => user !== null))
			.subscribe((user: User) => {
				// @note Capture that user belongs to a company, or checks within platform won't work.
				if (company) user.isCompany = true;
				const ctx = new AppContext(user, company);

				if (! skipReload) {
					this.reload(); // Try to reload first - line below allows other subscribers to change the URL afterward.
				}

				localStorage.setItem('currentContext', JSON.stringify(ctx));
				this.currentContext.next(ctx);
			});
	}
}
